import {
    ToastBar,
    Toaster,
    toast,
} from 'react-hot-toast'

import {
    Button as AriaButton,
} from 'react-aria-components'

import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const baseToastStyle = {
    width:
        'min(23rem, calc(100vw - 2rem))',

    minWidth:
        'min(20rem, calc(100vw - 2rem))',

    maxWidth: 'calc(100vw - 2rem)',

    padding: '11px 9px 11px 12px',

    borderRadius: '0.75rem',

    boxShadow: 'var(--shadow-popover)',

    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.35rem',

    wordBreak: 'break-word',
} as const

export function AppToaster() {
    const { t } = useTranslation()

    return (
        <Toaster
            position="top-right"
            reverseOrder={false}
            gutter={12}
            containerStyle={{
                top: 80,
                right: 16,
            }}
            toastOptions={{
                duration: 5_000,

                ariaProps: {
                    role: 'status',
                    'aria-live': 'polite',
                },

                style: {
                    ...baseToastStyle,

                    border:
                        '1px solid var(--toast-default-border)',

                    background:
                        'var(--toast-default-background)',

                    color:
                        'var(--toast-default-foreground)',
                },

                success: {
                    duration: 5_000,

                    ariaProps: {
                        role: 'status',
                        'aria-live': 'polite',
                    },

                    style: {
                        border:
                            '1px solid var(--toast-success-border)',
                        borderLeft:
                            '3px solid var(--toast-success-foreground)',

                        background:
                            'var(--toast-success-background)',

                        color:
                            'var(--toast-success-foreground)',
                    },

                    iconTheme: {
                        primary:
                            'var(--toast-success-foreground)',

                        secondary:
                            'var(--toast-success-background)',
                    },
                },

                error: {
                    duration: 7_000,

                    ariaProps: {
                        role: 'alert',
                        'aria-live': 'assertive',
                    },

                    style: {
                        border:
                            '1px solid var(--toast-error-border)',
                        borderLeft:
                            '3px solid var(--toast-error-foreground)',

                        background:
                            'var(--toast-error-background)',

                        color:
                            'var(--toast-error-foreground)',
                    },

                    iconTheme: {
                        primary:
                            'var(--toast-error-foreground)',

                        secondary:
                            'var(--toast-error-background)',
                    },
                },

                loading: {
                    duration: Infinity,

                    ariaProps: {
                        role: 'status',
                        'aria-live': 'polite',
                    },

                    style: {
                        border:
                            '1px solid var(--toast-loading-border)',
                        borderLeft:
                            '3px solid var(--toast-loading-foreground)',

                        background:
                            'var(--toast-loading-background)',

                        color:
                            'var(--toast-loading-foreground)',
                    },

                    iconTheme: {
                        primary:
                            'var(--toast-loading-foreground)',

                        secondary:
                            'var(--toast-loading-background)',
                    },
                },
            }}
        >
            {(currentToast) => (
                <ToastBar
                    toast={currentToast}
                >
                    {({
                        icon,
                        message,
                    }) => (
                        <>
                            <span
                                aria-hidden="true"
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black/[0.04] dark:bg-white/[0.08]"
                            >
                                {icon}
                            </span>

                            <div className="min-w-0 flex-1 pr-1">
                                {message}
                            </div>

                            <AriaButton
                                type="button"
                                aria-label={t(
                                    'ui.notifications.dismiss',
                                )}
                                onPress={() => {
                                    toast.dismiss(
                                        currentToast.id,
                                    )
                                }}
                                className={[
                                    'ml-1 inline-flex',
                                    'h-9 w-9 shrink-0',
                                    'items-center justify-center',
                                    'rounded-lg',

                                    'text-current opacity-70',
                                    'outline-none',

                                    'transition-[background-color,opacity]',
                                    'duration-150',

                                    'data-hovered:bg-black/10',
                                    'data-hovered:opacity-100',

                                    'data-pressed:bg-black/15',

                                    'data-focus-visible:opacity-100',
                                    'data-focus-visible:ring-2',
                                    'data-focus-visible:ring-current',
                                    'data-focus-visible:ring-offset-1',
                                    'data-focus-visible:ring-offset-transparent',

                                    'dark:data-hovered:bg-white/10',
                                    'dark:data-pressed:bg-white/15',
                                ].join(' ')}
                            >
                                <X
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                    strokeWidth={2.25}
                                />
                            </AriaButton>
                        </>
                    )}
                </ToastBar>
            )}
        </Toaster>
    )
}
